/**
 * Modular per-job-type detail block for the preview sheet (RM-118). Each job
 * type owns a renderer, resolved from {@link JOB_TYPE_DETAILS} by
 * `job_spec.type`. Adding a new job type = add its `JobSpec` variant + one entry
 * in the registry below; the sheet stays type-agnostic.
 *
 * This is the detail-view counterpart to the list's compact {@link JobSummary};
 * keep the two in sync when a type's fields change.
 */
import type { ReactNode } from 'react'
import type { Job, JobSpec } from '../../lib/schemas'
import { modeLabels } from '../../lib/jobView'

/** A definition row; dropped when its value is null/undefined/''. */
type Row = [label: string, value: ReactNode]

function DefinitionList({ rows }: { rows: Row[] }) {
  const shown = rows.filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  )
  if (shown.length === 0)
    return <p className="text-sm text-muted-foreground">Brak parametrow</p>
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {shown.map(([label, value], i) => (
        <div key={i} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

const str = (v: unknown) => (v == null ? undefined : String(v))

function BlenderDetails({ job }: { job: Job }) {
  const spec = job.job_spec
  if (spec.type !== 'blender_render') return null
  const mp = spec.mode_params?.[0] ?? {}
  const w = str(mp.width)
  const h = str(mp.height)
  const fs = str(mp.frame_start)
  const fe = str(mp.frame_end)
  const modes = modeLabels(job)
  return (
    <DefinitionList
      rows={[
        ['Tryby', modes.length > 0 ? modes.join(', ') : undefined],
        ['Silnik', str(mp.engine)],
        ['Rozdzielczosc', w && h ? `${w}×${h}` : undefined],
        ['Klatki', fs && fe ? `${fs}–${fe}` : undefined],
        ['Format', str(mp.output_format)],
        ['Sample', str(mp.samples) ? `${str(mp.samples)} spp` : undefined],
        ['GPU', spec.gpu_type],
        ['Blender', spec.blender_version],
      ]}
    />
  )
}

function UpscalingDetails({
  spec,
  priorityMode,
}: {
  spec: Extract<JobSpec, { type: 'upscaling' }>
  priorityMode: Job['priority_mode']
}) {
  const setting = (
    value: string | number | undefined,
    fallback = 'Domyślnie w workerze',
  ) => (value === undefined ? fallback : String(value))
  return (
    <DefinitionList
      rows={[
        ['View ID', <span className="font-mono text-xs">{spec.view_id}</span>],
        ['Preset', setting(spec.preset_name, 'Własne ustawienia')],
        ['Priorytet', priorityMode === 'boost' ? 'Boost' : 'Normalny'],
        [
          'Frame elements ID',
          setting(spec.frame_elements_id, 'Automatyczny wybór'),
        ],
        ['Upscaler główny', spec.main_upscaler],
        ['Wersja Topaza', setting(spec.topaz_version, 'Dowolna dostępna')],
        [
          'Skala główna Topaz',
          spec.topaz_upscale_factor === undefined
            ? 'Nie ustawiono'
            : `${spec.topaz_upscale_factor}×`,
        ],
        ['Siła główna Topaz', setting(spec.topaz_power, 'Nie ustawiono')],
        [
          'Docelowa szerokość',
          spec.target_size === undefined
            ? 'Natywna (najszersze źródło)'
            : `${spec.target_size}px`,
        ],
        [
          'Korekta jasności',
          spec.max_brightness_adjustment ? 'Włączona' : 'Wyłączona',
        ],
        [
          'Nakładka budynku',
          spec.investment_use_source
            ? 'Oryginalne klatki źródłowe'
            : (spec.investment_upscaler ?? 'Wyłączona'),
        ],
        [
          'Oryginalne źródło nakładki',
          spec.investment_use_source ? 'Tak' : 'Nie',
        ],
        [
          'Upscaler nakładki',
          setting(spec.investment_upscaler, 'Nie ustawiono'),
        ],
        [
          'Skala nakładki Topaz',
          spec.investment_upscale_factor === undefined
            ? 'Nie ustawiono'
            : `${spec.investment_upscale_factor}×`,
        ],
        [
          'Siła nakładki Topaz',
          setting(spec.investment_power, 'Nie ustawiono'),
        ],
        [
          'Feather maski',
          `${spec.merge_feather_px ?? 3}px${spec.merge_feather_px === undefined ? ' (domyślnie)' : ''}`,
        ],
        [
          'Interpolacja główna · Simple',
          setting(spec.main_simple_interpolation),
        ],
        [
          'Interpolacja główna · zmniejszanie',
          setting(spec.main_downscale_interpolation),
        ],
        [
          'Interpolacja główna · rozmiar końcowy',
          setting(spec.main_resize_interpolation),
        ],
        [
          'Interpolacja nakładki · Simple',
          setting(spec.investment_simple_interpolation),
        ],
        [
          'Interpolacja nakładki · zmniejszanie',
          setting(spec.investment_downscale_interpolation),
        ],
        [
          'Interpolacja nakładki · rozmiar końcowy',
          setting(spec.investment_resize_interpolation),
        ],
      ]}
    />
  )
}

function EmptyDetails({ spec }: { spec: Extract<JobSpec, { type: 'empty' }> }) {
  return (
    <DefinitionList
      rows={[
        ['Czas oczekiwania', `${spec.wait_secs}s`],
        [
          'Kod wyjscia',
          spec.exit_code !== undefined ? String(spec.exit_code) : undefined,
        ],
      ]}
    />
  )
}

function TurntableFrameDetails({
  spec,
}: {
  spec: Extract<JobSpec, { type: 'turntable_frame_bake' }>
}) {
  return (
    <DefinitionList
      rows={[
        [
          'Color view',
          <span className="font-mono text-xs">{spec.color_view_id}</span>,
        ],
        [
          'Depth view',
          <span className="font-mono text-xs">{spec.depth_view_id}</span>,
        ],
        ['Target', spec.output_target_id],
        ['Output frames', spec.output_frame_count],
        ['Manifest', `${spec.manifest.frames.length} source frames`],
      ]}
    />
  )
}

/**
 * Registry: one entry per job type. To add a type, add its `JobSpec` variant
 * and a renderer here — nothing else in the sheet needs to change.
 */
const JOB_TYPE_DETAILS: Record<JobSpec['type'], (job: Job) => ReactNode> = {
  surroundings: () => <p className="text-sm">Generowanie modelu otoczenia.</p>,
  blender_render: (job) => <BlenderDetails job={job} />,
  upscaling: (job) =>
    job.job_spec.type === 'upscaling' ? (
      <UpscalingDetails spec={job.job_spec} priorityMode={job.priority_mode} />
    ) : null,
  empty: (job) =>
    job.job_spec.type === 'empty' ? <EmptyDetails spec={job.job_spec} /> : null,
  turntable_frame_bake: (job) =>
    job.job_spec.type === 'turntable_frame_bake' ? (
      <TurntableFrameDetails spec={job.job_spec} />
    ) : null,
}

export function JobTypeDetails({ job }: { job: Job }) {
  const render = JOB_TYPE_DETAILS[job.job_spec.type]
  return render ? (
    <>{render(job)}</>
  ) : (
    <p className="text-sm text-muted-foreground">
      Nieznany typ zadania: {job.job_spec.type}
    </p>
  )
}
